import React, { FunctionComponent, useState, useEffect } from 'react';
import {
  useBoardExportQuery,
  useStartBoardExportMutation,
} from '@trello/graphql/generated';
import { PrimaryButton } from 'app/src/components/PrimaryButton';
import { forTemplate } from 'app/common/lib/util/i18n';
import styles from './ExportPopoverContent.less';
import { idToDate } from 'app/common/lib/util/date';
import { getDateDeltaString } from 'app/gamma/src/util/dates';
import { bytes } from 'app/common/lib/util/file-size';
import { Spinner } from 'app/src/components/Spinner';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics/src/AnalyticsClient';

const formatPopoverShare = forTemplate('popover_share');

interface ExportPopoverContentProps {
  idBoard: string;
  idOrg: string;
}

export const ExportPopoverContent: FunctionComponent<ExportPopoverContentProps> = ({
  idBoard,
  idOrg,
}) => {
  const { data, startPolling, stopPolling } = useBoardExportQuery({
    variables: {
      idBoard,
      idExport: 'mostRecent',
    },
  });

  const isFinished =
    data &&
    data.board &&
    data.board.exports[0] &&
    data.board.exports[0].status.finished;

  const [isExporting, setIsExporting] = useState(false);
  const [hasBeenExportedBefore, setHasBeenExportedBefore] = useState(
    data && data.board && !!data.board.exports[0],
  );

  useEffect(() => {
    if (!hasBeenExportedBefore) {
      setIsExporting(false);
    } else if (!isFinished) {
      setIsExporting(true);
      startPolling(1000);
    }
  }, [hasBeenExportedBefore, isFinished, startPolling]);

  useEffect(() => {
    if (isFinished) {
      setIsExporting(false);
      stopPolling();
    }
  }, [data, isFinished, stopPolling]);
  const [startBoardExport] = useStartBoardExportMutation();

  useEffect(() => {
    trackUe({
      category: Noun.BOARDS_MENU,
      verb: Verb.SHOWS,
      directObj: Noun.EXPORT_POPOVER,
      context: {
        boardId: idBoard,
        organizationId: idOrg,
      },
    });
    Analytics.sendScreenEvent({
      name: 'exportBoardInlineDialog',
    });
  }, [idBoard, idOrg]);

  if (!data || !data.board)
    return (
      <div>
        <Spinner centered />
      </div>
    );

  if (data && data.board && data.board.idOrganization)
    idOrg = data.board.idOrganization;

  const executeExport = () => {
    setHasBeenExportedBefore(true);
    setIsExporting(true);
    startBoardExport({
      variables: { id: idBoard },
    }).then(() => {
      startPolling(1000);
    });
    trackUe({
      category: Noun.BOARDS_MENU,
      verb: Verb.CLICKS,
      directObj: Noun.EXPORT_BOARD,
      context: {
        boardId: idBoard,
        organizationId: idOrg,
      },
    });
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'exportButton',
      source: 'exportBoardInlineDialog',
      containers: {
        board: {
          id: idBoard,
        },
        organization: {
          id: idOrg,
        },
      },
    });
  };

  const trackDownload = () => {
    trackUe({
      category: Noun.BOARDS_MENU,
      verb: Verb.CLICKS,
      directObj: Noun.DOWNLOAD_BOARD_EXPORT,
      context: {
        boardId: idBoard,
        organizationId: idOrg,
      },
    });
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'downloadLink',
      source: 'exportBoardInlineDialog',
      containers: {
        board: {
          id: idBoard,
        },
        organization: {
          id: idOrg,
        },
      },
    });
  };

  return (
    <div>
      {data.board.exports[0] &&
        data.board.exports[0].status.finished &&
        !isExporting && (
          <p>
            {formatPopoverShare('last-exported', {
              timeOfLastExport: getDateDeltaString(
                idToDate(data.board.exports[0].id),
                new Date(),
              ),
              sizeOfExport:
                typeof data.board.exports[0].size === 'number'
                  ? bytes(data.board.exports[0].size)
                  : 'unknown',
            })}
          </p>
        )}

      {isExporting && hasBeenExportedBefore && (
        <p>{formatPopoverShare('exporting-ellipsis')}</p>
      )}

      {!data.board.exports[0] && !hasBeenExportedBefore && (
        <p className={styles.exportHistoryText}>
          {formatPopoverShare('no-export-history')}
        </p>
      )}
      <div className={styles.buttons}>
        <PrimaryButton
          onClick={executeExport}
          className={styles.exportButton}
          disabled={isExporting}
        >
          {formatPopoverShare('export')}
        </PrimaryButton>

        {data.board.exports[0] && !isExporting && (
          <a
            className={styles.downloadLink}
            href={`/1/boards/${idBoard}/exports/${data.board.exports[0].id}/download`}
            onClick={trackDownload}
          >
            {formatPopoverShare('download')}
          </a>
        )}
      </div>
    </div>
  );
};
